import { useState, useEffect, useRef } from "react";
import { ChatMessage, ChatMessageRole, PromptType, TaskAiResponse } from "processEditor/components/Modals/AssistantModal";
import { BaseSignalRStrategy, NonStreamingSignalRStrategy, StreamingSignalRStrategy } from "./BaseSignalRStrategy";
import * as signalR from "@microsoft/signalr";

const useAzureAiSignalR = <T>(hubUrl: string, promptType: PromptType) => {
    const [completionInProgress, setCompletionStatus] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();
    const [completionMessage, setCompletionMessage] = useState<T | null>(null);

    const connectionRef = useRef<signalR.HubConnection | null>(null);  // Use ref for connection

    const [connectionReady, setConnectionReady] = useState<boolean>(false);
    const handleCompletionErrorTimeout = useRef<NodeJS.Timeout>();

    let strategy: BaseSignalRStrategy;
    switch (promptType) {
        case PromptType.CodeCompletion:
        case PromptType.DocumentationCompletion: {
            strategy = new StreamingSignalRStrategy(setCompletionMessage as React.Dispatch<React.SetStateAction<string>>);
            break;
        }
        case PromptType.TaskCompletion: {
            strategy = new NonStreamingSignalRStrategy<T>(setErrorMessage, setCompletionMessage);
            break;
        }
    }

    useEffect(() => {
        const newConnection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, { skipNegotiation: true, transport: signalR.HttpTransportType.WebSockets })
            .withAutomaticReconnect()
            .configureLogging(signalR.LogLevel.Error)
            .build();


        connectionRef.current = newConnection;
        const timeoutCurrent = handleCompletionErrorTimeout.current;
        setConnectionReady(true);
        return () => {
            if (connectionRef.current) {
                connectionRef.current.stop();
            }
            if (timeoutCurrent) {
                clearTimeout(timeoutCurrent);
            }
        };
    }, [hubUrl]);

    useEffect(() => {
        if (connectionRef.current && connectionReady) {
            connectionRef.current
                .start()
                .then(() => {
                    console.debug("Connected!");

                    connectionRef.current?.on("CompletionMessage", (message: string) => {
                        strategy.handleMessage(message);
                    });

                    connectionRef.current?.on("CompletionInProgress", (state: boolean) => {
                        setCompletionStatus(state);

                        if (state) {
                            strategy.resetOnCompletionStart();

                            handleCompletionErrorTimeout.current = setTimeout(() => {
                                setErrorMessage('Failed to process the request. Please try again.');
                                setCompletionStatus(false);
                            }, 30000);
                        } else if (!state && handleCompletionErrorTimeout.current) {
                            clearTimeout(handleCompletionErrorTimeout.current);
                            handleCompletionErrorTimeout.current = undefined;
                        }
                    });
                })
                .catch((error) => console.error("Connection failed: ", error));
        }
    }, [connectionReady]);

    const ensureConnectionStarted = async (): Promise<void> => {
        if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
            // If a connection attempt is already in progress, wait for it to finish
            return new Promise((resolve) => {
                const interval = setInterval(() => {
                    if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
                        clearInterval(interval);
                        resolve();
                    }
                }, 100); // Polling interval to check if connection is ready
            });
        }
    };

    const startSession = async (sessionId: string): Promise<void> => {
        console.debug("Start session");
        await ensureConnectionStarted();
        if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
            await connectionRef.current.invoke("RegisterCompletion", sessionId);
        }
    };

    const promptCompletion = async (sessionId: string, promptType: PromptType, context: unknown, messages: ChatMessage[]): Promise<void> => {
        console.debug("Prompt code completion");
        await ensureConnectionStarted();
        if (connectionRef.current && connectionRef.current.state === signalR.HubConnectionState.Connected) {
            // Itarete messages array and transofrm all non string messages to string
            const transformedMessages = messages.map((msg) => {
                if (msg.role === ChatMessageRole.Assistant) {
                    return {
                        ...msg,
                        message: stringifyMessageIfNotString(msg.message),
                    };
                }
                // If it's a UserChatMessage, message is already a string, so no need to transform
                return msg;
            });
            await connectionRef.current.invoke("AzureAiCompletion", { sessionId: sessionId, referer: window.location.href, promptType: promptType, frendsContext: context, promptMessages: transformedMessages });
        }
    };


    function stringifyMessageIfNotString(message: string | TaskAiResponse | null): string {
        if (typeof message === 'string') {
            return message;
        } else if (message === null) {
            return "";
        } else {
            return JSON.stringify(message);
        }
    }

    return {
        completionInProgress,
        errorMessage,
        completionMessage,
        startSession: startSession,
        promptCompletion: promptCompletion
    };
};

export default useAzureAiSignalR;